import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DialogModal } from "@zfp-akademie-a-s/zfp-design";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AddIcon, DeleteIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { type HousingExpenseFormType, getHousingExpenseFormSchema } from "Forms/Schemas/Budget/Expenses/housingExpenseFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getGetHousingExpenseQueryKey,
  useGetHousingExpense,
  useReplaceHousingExpense,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type HousingAmount, HousingAmountAmountType, HousingExpenseEntryMode } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type RefObject, forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ExpenseFormHandle } from "../Components/EditExpenseDrawer";

type Props = {
  onClose: () => void;
  ref?: RefObject<ExpenseFormHandle>;
};

export const HousingExpenseForm = forwardRef<ExpenseFormHandle, Props>(({ onClose }, ref) => {
  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const [detailed, setDetailed] = useState(true);
  const [openDeleteBulkModal, setOpenDeleteBulkModal] = useState<boolean>(false);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();
  const { data: housingExpenseData } = useGetHousingExpense(analysisId, versionId);
  const { mutateAsync: replaceHousingExpenseAsync } = useReplaceHousingExpense();

  const { control, setValue, watch, handleSubmit, clearErrors } = useForm<HousingExpenseFormType>({
    resolver: yupResolver(getHousingExpenseFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      aggregatedAmount: (housingExpenseData?.aggregatedAmount?.value.toString() as any) || "",
      customItems: [],
      detailed: true,
    },
  });

  const {
    fields: customFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "customItems",
  });

  const customItems = watch("customItems");
  const rent = watch("rent");
  const electricity = watch("electricity");
  const gas = watch("gas");
  const water = watch("water");

  useEffect(() => {
    if (detailed) {
      const predefinedFieldsSum =
        (Number.parseFloat(rent ?? "0") || 0) +
        (Number.parseFloat(electricity ?? "0") || 0) +
        (Number.parseFloat(gas ?? "0") || 0) +
        (Number.parseFloat(water ?? "0") || 0);

      const customItemsSum =
        customItems?.reduce((sum, item) => {
          const value = Number.parseFloat(item.value ?? "0") || 0;
          return sum + value;
        }, 0) || 0;

      const total = predefinedFieldsSum + customItemsSum;
      setValue("aggregatedAmount", total.toString());
    }
  }, [customItems, rent, electricity, gas, water, detailed, setValue]);

  const recalculateTotal = () => {
    if (detailed) {
      const predefinedFieldsSum =
        (Number.parseFloat(rent ?? "0") || 0) +
        (Number.parseFloat(electricity ?? "0") || 0) +
        (Number.parseFloat(gas ?? "0") || 0) +
        (Number.parseFloat(water ?? "0") || 0);

      const customItemsSum =
        customItems?.reduce((sum, item) => {
          const value = Number.parseFloat(item.value ?? "0") || 0;
          return sum + value;
        }, 0) || 0;

      const total = predefinedFieldsSum + customItemsSum;
      setValue("aggregatedAmount", total.toString());
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteExpense: () => {
      setOpenDeleteBulkModal(true);
    },
  }));

  useEffect(() => {
    if (housingExpenseData) {
      handleChangeDetailed(housingExpenseData.entryMode === HousingExpenseEntryMode.individual);
      setValue("aggregatedAmount", (housingExpenseData.aggregatedAmount?.value ?? 0).toString());

      const customItems: { name: string; value: string }[] = [];
      for (const expense of housingExpenseData.amounts) {
        switch (expense.amountType) {
          case HousingAmountAmountType.rent:
            setValue("rent", expense.amount.value.toString());
            break;
          case HousingAmountAmountType.electricity:
            setValue("electricity", expense.amount.value.toString());
            break;
          case HousingAmountAmountType.gas:
            setValue("gas", expense.amount.value.toString());
            break;
          case HousingAmountAmountType.water:
            setValue("water", expense.amount.value.toString());
            break;
          case HousingAmountAmountType.other:
            customItems.push({
              name: expense.name,
              value: expense.amount.value.toString(),
            });
            break;
        }
      }
      replace(customItems);
    }
  }, [housingExpenseData, setValue, replace]);

  const onSubmit = async (data: HousingExpenseFormType) => {
    if (Number(data.aggregatedAmount) === 0) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseAmountZeroError));
      return;
    }

    const customItems: HousingAmount[] =
      data.customItems?.map((field) => ({
        amountType: HousingAmountAmountType.other,
        amount: {
          value: Number.parseFloat(field.value ?? "0") || 0,
          currencyCode: "CZK",
          frequency: AmountFrequency.monthly,
        },
        name: field.name ?? "",
      })) ?? [];

    const amounts: HousingAmount[] = detailed
      ? [
          {
            amountType: HousingAmountAmountType.rent,
            amount: {
              value: Number.parseFloat(data.rent ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Rent),
          },
          {
            amountType: HousingAmountAmountType.electricity,
            amount: {
              value: Number.parseFloat(data.electricity ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Electricity),
          },
          {
            amountType: HousingAmountAmountType.gas,
            amount: {
              value: Number.parseFloat(data.gas ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Gas),
          },
          {
            amountType: HousingAmountAmountType.water,
            amount: {
              value: Number.parseFloat(data.water ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Water),
          },
          ...customItems,
        ]
      : [];

    try {
      await replaceHousingExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? HousingExpenseEntryMode.individual : HousingExpenseEntryMode.aggregate,
          aggregatedAmount: {
            value: Number.parseFloat(data.aggregatedAmount ?? "0") || 0,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          amounts,
        },
      });

      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    } finally {
      queryClient.invalidateQueries({ queryKey: getGetHousingExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
    }
  };

  const onDelete = async () => {
    try {
      await replaceHousingExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? HousingExpenseEntryMode.individual : HousingExpenseEntryMode.aggregate,
          aggregatedAmount: { value: 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
          amounts: [],
        },
      });

      queryClient.invalidateQueries({ queryKey: getGetHousingExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const onDeleteBulkExpense = () => {
    replace([]);
    setOpenDeleteBulkModal(false);
    onDelete();
  };

  const handleChangeDetailed = (value: boolean) => {
    setDetailed(value);
    setValue("detailed", value);
    clearErrors();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
        <Box>
          <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
            {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.Housing)}
          </Typography>

          <Grid container spacing={Spacing.XL}>
            <Grid size={6}>
              <FormNumberField
                control={control}
                name="aggregatedAmount"
                label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.Aggregated)}
                valueType="text"
                scale={2}
                min={0}
                thousandsSeparator=" "
                unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                disabled={detailed || readOnly}
                placeholder="0"
                fullWidth
              />
            </Grid>

            <Grid size={6}>
              <Box display="flex" alignItems="flex-start" mt={5} height="100%">
                <FormControlLabel
                  control={<Checkbox checked={detailed} disabled={readOnly} onChange={(_, checked) => handleChangeDetailed(checked)} />}
                  label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.DetailedCheckbox)}
                />
              </Box>
            </Grid>

            {detailed && (
              <>
                <Grid size={6}>
                  <TextField
                    label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Item)}
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Rent)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="rent"
                    label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Value)}
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Electricity)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="electricity"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Gas)} disabled fullWidth />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="gas"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Water)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="water"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                {customFields.map((field, idx) => (
                  <React.Fragment key={field.id}>
                    <Grid size={6}>
                      <FormTextField
                        control={control}
                        name={`customItems.${idx}.name`}
                        disabled={readOnly}
                        placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.OwnName)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={5}>
                      <FormNumberField
                        control={control}
                        name={`customItems.${idx}.value`}
                        valueType="text"
                        scale={2}
                        min={0}
                        thousandsSeparator=" "
                        unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                        disabled={readOnly}
                        placeholder="0"
                        onChange={recalculateTotal}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={1} display="flex" alignItems="flex-start" justifyContent="center">
                      <Button disabled={readOnly} variant="text" color="error" onClick={() => remove(idx)}>
                        <DeleteIcon />
                      </Button>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid size={12}>
                  <Button variant="text" disabled={readOnly} startIcon={<AddIcon />} onClick={() => append({ name: "", value: "" })}>
                    {t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.AddCustomItem)}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Stack>

      <DialogModal
        open={openDeleteBulkModal}
        onClose={() => setOpenDeleteBulkModal(false)}
        closeLabel={t(Resources.Common.Close)}
        title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpense)}
        subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseHint)}
        primaryAction={
          <Button variant="contained" disabled={readOnly} onClick={onDeleteBulkExpense}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </form>
  );
});
